'use client'
import { getProfileDetail } from "@/apis/profile"
import { useUserDetailStore } from "@/store/useUserDetailStore"
import { useCallback, useEffect, useState } from "react"
import {
  canShowPendingSubscribeFail,
  clearPendingPremiumSubscribe,
  hasMembership,
  markPendingSubscribeShown,
  peekPendingPremiumSubscribe,
} from "./pendingSubscribe"

export type SubscribeFailCheckStatus = "checking" | "shown" | "idle"

// 跨 Strict Mode remount 保留结果，避免过期逻辑把「检查中」又当成可以弹
let shownThisVisit = false
let failCheckResult: SubscribeFailCheckStatus | null = null

export const usePendingSubscribeFail = (onFail: () => void) => {
  const [status, setStatus] = useState<SubscribeFailCheckStatus>(failCheckResult ?? "checking")
  const setDetail = useUserDetailStore(state => state.setDetail)

  const markIdle = useCallback(() => {
    if (failCheckResult === "shown") return
    failCheckResult = "idle"
    setStatus("idle")
  }, [])

  const check = useCallback(async () => {
    if (shownThisVisit) return
    const pending = peekPendingPremiumSubscribe()
    if (!pending || !canShowPendingSubscribeFail()) {
      markIdle()
      return
    }
    if (pending.hadMembership) {
      clearPendingPremiumSubscribe()
      markIdle()
      return
    }

    shownThisVisit = true
    failCheckResult = "checking"
    setStatus("checking")
    try {
      const res = await getProfileDetail()
      if (res.status === 200 && res.data) {
        setDetail(res.data)
        if (hasMembership(res.data)) {
          clearPendingPremiumSubscribe()
          markIdle()
          return
        }
      }
      markPendingSubscribeShown()
      failCheckResult = "shown"
      setStatus("shown")
      onFail()
    } catch {
      shownThisVisit = false
      markIdle()
    }
  }, [markIdle, onFail, setDetail])

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") void check()
    }
    document.addEventListener("visibilitychange", onVisible)
    window.addEventListener("pageshow", onVisible)
    void check()
    return () => {
      document.removeEventListener("visibilitychange", onVisible)
      window.removeEventListener("pageshow", onVisible)
    }
  }, [check])

  return status
}
